import React, {useState} from "react";
import {useSelector} from "react-redux";
import {useFormikContext} from "formik";

import {IStore} from "../../app/reducers/hybrid_reducer";

const validateFile = (file: File) => {
    const errors: string[] = [];
    if (file.size > 5000000) {
        errors.push("Plik jest zbyt duży. Maksymalny rozmiar to 5MB.");
    }
    if (!/image\/(jpg|jpeg|png|gif)$/i.test(file.type)) {
        errors.push("Plik musi być w formacie JPG, JPEG, PNG lub GIF.");
    }
    return errors;
};

interface UploadResponse {
    url: string;
    filename: string;
}

const UploadImageToS3WithNativeSdk = () => {
    const {setFieldValue} = useFormikContext<{fileKeys: string[]; gallery_order: string[]}>();
    const userData = useSelector((store: IStore) => store.dashboard.userData);
    const [items, setItems] = useState<Array<{key: string; preview: string}>>([]);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [uploading, setUploading] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    const syncItems = (nextItems: Array<{key: string; preview: string}>) => {
        setItems(nextItems);
        setFieldValue(
            "fileKeys",
            nextItems.map((item) => item.key)
        );
        setFieldValue(
            "gallery_order",
            nextItems.map((item) => `s3:${item.key}`)
        );
    };

    const createPreview = (file: File) =>
        new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
        });

    const uploadFile = async (file: File): Promise<{key: string; preview: string} | null> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("userID", userData.data.user_id?.toString() || "");
        try {
            const response = await fetch("https://rw-web-api.eu/wp-json/wp/v2/upload_gallery_to_s3/", {
                method: "POST",
                body: formData
            });

            const data: UploadResponse = await response.json();

            if (response.ok) {
                return {key: data.filename, preview: data.url || (await createPreview(file))};
            } else {
                console.error("Error uploading file:", data);
                setErrors((currentErrors) => [...currentErrors, `Nie udało się dodać pliku ${file.name}.`]);
            }
        } catch (error) {
            console.warn("err on uploadToS3", error);
            setErrors((currentErrors) => [...currentErrors, `Nie udało się dodać pliku ${file.name}.`]);
        }
        return null;
    };

    const handleRemoveImage = async (key: string) => {
        const formData = new FormData();
        formData.append("key", key);
        try {
            const response = await fetch("https://rw-web-api.eu/wp-json/wp/v2/delete_from_s3/", {
                method: "POST",
                body: formData
            });

            const data: any = await response.json();
            if (response.ok) {
                syncItems(items.filter((item) => item.key !== key));
            } else {
                console.error("Error deleting file:", data);
                setErrors(["Nie udało się usunąć zdjęcia."]);
            }
        } catch (error) {
            console.warn("err on delete from s3 front ", error);
            setErrors(["Nie udało się usunąć zdjęcia."]);
        }
    };

    const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) {
            return;
        }

        setUploading(true);
        setErrors([]);
        const nextItems = [...items];
        for (const file of Array.from(files)) {
            const fileErrors = validateFile(file);
            if (fileErrors.length > 0) {
                setErrors((currentErrors) => [...currentErrors, ...fileErrors.map((message) => `${file.name}: ${message}`)]);
                continue;
            }
            const uploadedItem = await uploadFile(file);
            if (uploadedItem) {
                nextItems.push(uploadedItem);
                syncItems([...nextItems]);
            }
        }
        setUploading(false);
        e.target.value = "";
    };

    const handleDrop = (targetIndex: number) => {
        if (draggedIndex === null || draggedIndex === targetIndex) {
            setDraggedIndex(null);
            return;
        }
        const nextItems = [...items];
        const [movedItem] = nextItems.splice(draggedIndex, 1);
        nextItems.splice(targetIndex, 0, movedItem);
        syncItems(nextItems);
        setDraggedIndex(null);
    };

    return (
        <div>
            <label>
                Dodaj zdjęcia
                <input type="file" accept="image/jpeg,image/png,image/gif" multiple onChange={handleFileInput} disabled={uploading} />
            </label>
            <p style={{margin: "8px 0"}}>Przeciągnij zdjęcia, aby zmienić kolejność. Pierwsze zdjęcie jest zdjęciem głównym.</p>
            {errors.map((error, index) => (
                <p key={`${error}-${index}`} style={{color: "red"}}>
                    {error}
                </p>
            ))}
            <div style={{display: "flex", flexWrap: "wrap", gap: "12px"}}>
                {items.map((item, index) => (
                    <div
                        key={item.key}
                        draggable
                        onDragStart={() => setDraggedIndex(index)}
                        onDragEnd={() => setDraggedIndex(null)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={() => handleDrop(index)}
                        style={{
                            padding: "8px",
                            cursor: "grab",
                            textAlign: "center",
                            border: index === 0 ? "2px solid #1769aa" : "1px solid #bbb",
                            borderRadius: "6px",
                            opacity: draggedIndex === index ? 0.5 : 1
                        }}
                    >
                        <img
                            src={item.preview}
                            alt={`Zdjęcie ${index + 1}`}
                            style={{display: "block", width: "120px", height: "100px", objectFit: "cover", marginBottom: "8px"}}
                        />
                        {index === 0 && <strong style={{display: "block", marginBottom: "6px"}}>Główne</strong>}
                        <button type="button" onClick={() => handleRemoveImage(item.key)} style={{backgroundColor: "red", color: "white"}}>
                            Usuń
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default UploadImageToS3WithNativeSdk;
